/**
 * One width policy for every full-page dialog in the app.
 *
 * Vendora is used on phones, where a dialog should take the whole screen — a floating card with
 * margins wastes the little width there is, and the price/discount tables were being squeezed
 * into about 326px because of it. On a desktop monitor the opposite problem appears: a form
 * stretched across 27 inches looks broken. So: edge-to-edge below 600px, and a centred 720px
 * column above it.
 *
 * Spread onto a <Dialog>:
 *     <Dialog open={open} onClose={onClose} {...APP_DIALOG}>
 *
 * Deliberately does NOT use MUI's `fullScreen` prop. That prop hard-codes width/height to 100%
 * and margin to 0, which then has to be fought back with overrides on every desktop breakpoint.
 * Driving it from PaperProps instead keeps the whole rule in one place and readable.
 *
 * On a phone the dialog fills the screen, and the app draws under the system bars
 * (viewport-fit=cover), so the paper itself keeps clear of the gesture/home bar at the bottom
 * and the notch at the sides in landscape. The top is the title's job (APP_DIALOG_TITLE_SX),
 * because the title's colour has to run up behind the status bar. Do not add the bottom inset
 * again on a dialog's own actions bar — it would be counted twice.
 *
 * Confirmation boxes ("Delete this?") stay small and centred — they are a question, not a task —
 * so they keep maxWidth="xs" rather than using this.
 */
export const APP_DIALOG_MAX_WIDTH = 720;

export const APP_DIALOG = {
    fullWidth: true,
    maxWidth: false,
    scroll: 'paper',
    PaperProps: {
        sx: {
            width: '100%',
            maxWidth: { xs: '100%', sm: APP_DIALOG_MAX_WIDTH },
            m: { xs: 0, sm: 4 },
            height: { xs: '100%', sm: 'auto' },
            maxHeight: { xs: '100%', sm: 'calc(100% - 64px)' },
            borderRadius: { xs: 0, sm: 2 },
            pb: { xs: 'env(safe-area-inset-bottom, 0px)', sm: 0 },
            pl: { xs: 'env(safe-area-inset-left, 0px)', sm: 0 },
            pr: { xs: 'env(safe-area-inset-right, 0px)', sm: 0 },
        },
    },
};

/**
 * Header bar for every dialog that has one — one look across the app.
 *
 * - Raised, not painted on: a slight top-to-bottom gradient and a soft shadow, so it sits above
 *   the list that scrolls under it instead of reading as one flat blue block.
 * - Buttons in it (close ✕, back ←) sit in a faint white circle, so they read as buttons; a bare
 *   thin ✕ on blue didn't. Styled here, so a dialog only has to put an IconButton in its title.
 * - A caption under the title (customer name, count) is a little dimmer, so the title leads.
 * - The notch/status-bar inset is respected so the title isn't hidden under it on a phone.
 *
 * Use on a <DialogTitle>, or on a <Box> for a dialog that builds its header by hand.
 * Small centred dialogs use DIALOG_TITLE_SX below (same look, no notch padding).
 */
const TITLE_BASE_SX = {
    position: 'relative',
    zIndex: 2,   // the shadow falls on the content below, not under it
    background: (theme) => `linear-gradient(180deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
    color: 'white',
    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.22)',
    pt: 1.5,
    pb: 1.5,
    '& .MuiIconButton-root': {
        color: 'white',
        width: 40,
        height: 40,
        flexShrink: 0,
        bgcolor: 'rgba(255, 255, 255, 0.16)',
        '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.26)' },
        '&:active': { bgcolor: 'rgba(255, 255, 255, 0.34)' },
        '&.Mui-disabled': { color: 'rgba(255, 255, 255, 0.5)', bgcolor: 'rgba(255, 255, 255, 0.08)' },
    },
    // Text in the header is always white — some headers are a plain Box, and text that set its
    // own colour (text.primary, text.secondary) came out dark on the blue.
    '& .MuiTypography-root': { color: 'white' },
    '& .MuiTypography-caption': { color: 'rgba(255, 255, 255, 0.82)' },
};

/** Full-page dialog header: the notch/status-bar inset is kept clear on a phone. */
export const APP_DIALOG_TITLE_SX = {
    ...TITLE_BASE_SX,
    pt: { xs: 'calc(12px + env(safe-area-inset-top, 0px))', sm: 2 },
};

/**
 * Small centred dialog header (a form, with a ✕) — same look as the full-page one. Centred, so
 * no notch padding. MUI removes the content's top padding right under a title, which is fine
 * on white but puts text flush against a coloured bar, so it is given back here.
 * Yes/no questions ("Delete this?") keep a plain white title.
 */
export const DIALOG_TITLE_SX = {
    ...TITLE_BASE_SX,
    '& + .MuiDialogContent-root.MuiDialogContent-root': { pt: 2 },
};
